import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Snackbar,
  TablePagination,
  Chip,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { 
  fetchPurchaseOrdersStart, 
  deletePurchaseOrder, 
  clearError,
  setSelectedPurchaseOrder 
} from '../../store/reducers/purchaseOrderSlice';
import { PurchaseOrder } from '../../services/purchaseOrderApi';

const PurchaseOrderList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { purchaseOrders, loading, error, total, page, limit } = useSelector((state: RootState) => state.purchaseOrder);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    dispatch(fetchPurchaseOrdersStart({ page: 1, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (searchTerm) {
        dispatch(fetchPurchaseOrdersStart({ page: 1, limit: 10, search: searchTerm }));
      } else {
        dispatch(fetchPurchaseOrdersStart({ page: 1, limit: 10 }));
      }
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm, dispatch]);

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(fetchPurchaseOrdersStart({ page: newPage + 1, limit }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(fetchPurchaseOrdersStart({ page: 1, limit: parseInt(event.target.value, 10) }));
  };

  const handleAddNew = () => {
    navigate('/purchaseorders/new');
  };

  const handleEdit = (purchaseOrder: PurchaseOrder) => {
    dispatch(setSelectedPurchaseOrder(purchaseOrder));
    navigate(`/purchaseorders/edit/${purchaseOrder.id}`);
  };

  const handleDelete = (purchaseOrderId: string) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      dispatch(deletePurchaseOrder(purchaseOrderId));
    }
  };

  const handleView = (purchaseOrder: PurchaseOrder) => {
    dispatch(setSelectedPurchaseOrder(purchaseOrder));
    navigate(`/purchaseorders/view/${purchaseOrder.id}`);
  };

  const handleClose = (purchaseOrder: PurchaseOrder) => {
    if (window.confirm('Are you sure you want to close this purchase order?')) {
      // TODO: Implement close functionality
      console.log('Close purchase order:', purchaseOrder.id);
    }
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case 1: return 'default'; // Draft
      case 2: return 'primary'; // Pending
      case 3: return 'secondary'; // Approved
      case 4: return 'default'; // Rejected
      case 5: return 'default'; // Closed
      default: return 'default';
    }
  };

  if (loading && purchaseOrders.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Purchase Orders
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add New Purchase Order
        </Button>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={error}
      />

      <Paper>
        <Box p={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search purchase orders by number or client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Purchase Order #</TableCell>
                <TableCell>Order Quantity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>PO Date</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseOrders.map((po) => (
                <TableRow key={po.id}>
                  <TableCell>{po.id}</TableCell>
                  <TableCell>{po.Number}</TableCell>
                  <TableCell>{po.OrderQty}</TableCell>
                  <TableCell>
                    <Chip
                      label={po.POStatus?.Name || 'Unknown'}
                      color={getStatusColor(po.PoStatusId)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{po.Client?.Name || po.ClientId}</TableCell>
                  <TableCell>{new Date(po.PoDate).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(po.DeliveryDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => handleView(po)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(po)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                      <IconButton size="small" onClick={() => handleClose(po)}>
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(po.id!)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default PurchaseOrderList; 